import React from 'react'

import { Icon, Textfield } from '@clientbase/clientbase-library'
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'

import { ExtraFieldItem } from 'components/ExtraFieldsManager'
import SortableExtraFieldItem from 'components/ExtraFieldsManager/SortableExtraFieldItem'

import { theme } from 'styles/theme'

import FieldDisplaySettings from './FieldDisplaySettings'
import {
  BASIC_SECTION,
  DEFAULT_MAX_ITEMS,
  EXTRAS_SECTION,
  FieldDisplaySettings as Settings,
  FormSection,
  MAX_ITEMS_LIMIT,
  sanitizeDisplaySettings,
  sectionKeys,
  sectionOf
} from './formLayout.utils'

interface ExtraFieldsBySectionProps {
  fields: ExtraFieldItem[]
  setFields: React.Dispatch<React.SetStateAction<ExtraFieldItem[]>>
  sections: FormSection[]
  setSections: React.Dispatch<React.SetStateAction<FormSection[]>>
  basicFields: Record<string, boolean>
  onToggleRequired: (id: string, checked: boolean) => void
  onDelete: (id: string) => void
  onEdit?: (id: string) => void
  onAddToSection?: (sectionKey: string) => void
}

const GroupTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="smBold"
    color={theme.palette.neutral[200]}
    display="block"
    mb={0.5}
  >
    {children}
  </Typography>
)

// SC-11487: campos extras do link agrupados como aparecem no formulário
// público: "Dados básicos", as seções do lojista e "Informações extras".
// Arrastar um campo para cima de outro de outra seção move ele de seção.
const ExtraFieldsBySection = ({
  fields,
  setFields,
  sections,
  setSections,
  basicFields,
  onToggleRequired,
  onDelete,
  onEdit,
  onAddToSection
}: ExtraFieldsBySectionProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  // Sem seção e sem campo em "Dados básicos" a lista fica como sempre foi
  const grouped =
    sections.length > 0 ||
    fields.some((field) => sectionOf(field) === BASIC_SECTION)

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return

    setFields((items) => {
      const oldIndex = items.findIndex((item) => item.uuid === active.id)
      const newIndex = items.findIndex((item) => item.uuid === over.id)
      if (oldIndex === -1 || newIndex === -1) return items

      const target = sectionOf(items[newIndex])
      const moved = items.map((item, index) =>
        index === oldIndex && sectionOf(item) !== target
          ? {
              ...item,
              display_settings: {
                ...item.display_settings,
                section: target || undefined
              }
            }
          : item
      )

      return sanitizeDisplaySettings(
        arrayMove(moved, oldIndex, newIndex),
        sections
      )
    })
  }

  const updateSettings = (uuid: string, settings: Settings) => {
    setFields((items) =>
      items.map((item) =>
        item.uuid === uuid ? { ...item, display_settings: settings } : item
      )
    )
  }

  const updateSection = (uuid: string, patch: Partial<FormSection>) => {
    const next = sections.map((section) =>
      section.uuid === uuid ? { ...section, ...patch } : section
    )
    setSections(next)
    // Desligar o botão de cópia (ou tornar a seção repetível) tira a ligação
    // dos campos com o contratante
    if (patch.copy_customer_data === false) {
      setFields((items) => sanitizeDisplaySettings(items, next))
    }
  }

  const removeSection = (uuid: string) => {
    const next = sections.filter((section) => section.uuid !== uuid)
    setSections(next)
    // Os campos da seção voltam para "Informações extras"
    setFields((items) => sanitizeDisplaySettings(items, next))
  }

  const renderFields = (items: ExtraFieldItem[]) => (
    <SortableContext
      items={items.map((item) => item.uuid)}
      strategy={verticalListSortingStrategy}
    >
      {items.map((field) => (
        <SortableExtraFieldItem
          key={field.uuid}
          id={field.uuid}
          extraField={field}
          onToggleRequired={onToggleRequired}
          onDelete={onDelete}
          onEdit={onEdit}
        >
          <FieldDisplaySettings
            field={field}
            fields={fields}
            sections={sections}
            basicFields={basicFields}
            onChange={(settings) => updateSettings(field.uuid, settings)}
          />
        </SortableExtraFieldItem>
      ))}
    </SortableContext>
  )

  const validFields = fields.filter((field) => field.uuid)

  if (!grouped) {
    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <Box width="100%" marginBottom={validFields.length > 0 ? '16px' : '0'}>
          {renderFields(validFields)}
        </Box>
      </DndContext>
    )
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <Stack width="100%" gap={2} mb={2}>
        {sectionKeys(sections).map((key) => {
          const items = validFields.filter((field) => sectionOf(field) === key)
          const section = sections.find((s) => s.uuid === key)

          if (key === BASIC_SECTION && items.length === 0) return null

          return (
            <Box
              key={key || 'extras'}
              data-testid={`form-section-${key || 'extras'}`}
              p={1.5}
              borderRadius="8px"
              bgcolor={section ? '#FAFBFA' : 'transparent'}
              border={
                section ? `1px solid ${theme.palette.neutral[700]}` : 'none'
              }
            >
              {key === BASIC_SECTION && <GroupTitle>Dados básicos</GroupTitle>}
              {key === EXTRAS_SECTION && (
                <GroupTitle>Informações extras</GroupTitle>
              )}
              {section && (
                <Stack gap={0.5} mb={1}>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Box flexGrow={1}>
                      <Textfield
                        size="small"
                        fullWidth
                        value={section.title}
                        placeholder="Nome da seção (ex.: Dados do aluno)"
                        inputProps={{
                          'aria-label': 'Nome da seção',
                          maxLength: 100
                        }}
                        onChange={(event) =>
                          updateSection(section.uuid, {
                            title: event.target.value
                          })
                        }
                      />
                    </Box>
                    <Tooltip title="Remover seção" arrow placement="top">
                      <IconButton
                        size="small"
                        aria-label="Remover seção"
                        onClick={() => removeSection(section.uuid)}
                      >
                        <Icon
                          icon="delete"
                          sx={{
                            fontSize: '18px',
                            color: theme.palette.neutral[400]
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={!!section.repeatable}
                        onChange={(e) =>
                          updateSection(
                            section.uuid,
                            e.target.checked
                              ? {
                                  repeatable: true,
                                  copy_customer_data: false,
                                  item_label: section.item_label ?? '',
                                  max_items:
                                    section.max_items || DEFAULT_MAX_ITEMS
                                }
                              : { repeatable: false }
                          )
                        }
                      />
                    }
                    label={
                      <Typography
                        variant="smLight"
                        color={theme.palette.neutral[400]}
                      >
                        Permitir cadastrar mais de um (ex.: Filho 1, Filho 2)
                      </Typography>
                    }
                  />
                  {section.repeatable && (
                    <Stack
                      direction="row"
                      gap={1.5}
                      flexWrap="wrap"
                      ml="30px"
                      mb={0.5}
                    >
                      <Box flex="1 1 220px">
                        <Textfield
                          size="small"
                          fullWidth
                          label="Nome de cada item"
                          value={section.item_label ?? ''}
                          placeholder={section.title.trim() || 'Filho'}
                          inputProps={{
                            'aria-label': 'Nome de cada item',
                            maxLength: 50
                          }}
                          helperText={`Aparece como "${
                            (section.item_label ?? '').trim() ||
                            section.title.trim() ||
                            'Filho'
                          } 1", "${
                            (section.item_label ?? '').trim() ||
                            section.title.trim() ||
                            'Filho'
                          } 2"...`}
                          onChange={(event) =>
                            updateSection(section.uuid, {
                              item_label: event.target.value
                            })
                          }
                        />
                      </Box>
                      <Box flex="0 0 140px">
                        <Textfield
                          size="small"
                          fullWidth
                          type="number"
                          label="Até quantos"
                          value={section.max_items ?? DEFAULT_MAX_ITEMS}
                          inputProps={{
                            'aria-label': 'Até quantos itens',
                            min: 2,
                            max: MAX_ITEMS_LIMIT
                          }}
                          onChange={(event) =>
                            updateSection(section.uuid, {
                              max_items: Number(event.target.value)
                            })
                          }
                        />
                      </Box>
                    </Stack>
                  )}
                  {!section.repeatable && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={section.copy_customer_data}
                          onChange={(e) =>
                            updateSection(section.uuid, {
                              copy_customer_data: e.target.checked
                            })
                          }
                        />
                      }
                      label={
                        <Typography
                          variant="smLight"
                          color={theme.palette.neutral[400]}
                        >
                          Mostrar botão &quot;Mesmos dados do contratante&quot;
                        </Typography>
                      }
                    />
                  )}
                </Stack>
              )}
              {items.length > 0 ? (
                renderFields(items)
              ) : (
                <Typography
                  variant="xsLight"
                  color={theme.palette.neutral[400]}
                  display="block"
                >
                  {section
                    ? 'Nenhum campo nesta seção. Escolha a seção em "Exibição" de cada campo.'
                    : 'Nenhum campo fora de seção.'}
                </Typography>
              )}
              {section && onAddToSection && (
                <Stack
                  role="button"
                  tabIndex={0}
                  direction="row"
                  alignItems="center"
                  gap="5px"
                  mt={1}
                  width="fit-content"
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onAddToSection(section.uuid)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onAddToSection(section.uuid)
                    }
                  }}
                >
                  <Icon
                    icon="add"
                    sx={{ color: theme.palette.neutral[400], fontSize: '16px' }}
                  />
                  <Typography
                    variant="smLight"
                    color={theme.palette.neutral[400]}
                  >
                    Adicionar campo nesta seção
                  </Typography>
                </Stack>
              )}
            </Box>
          )
        })}
      </Stack>
    </DndContext>
  )
}

export default ExtraFieldsBySection
